import type { Issue, BlockerEntry, BlockerRef, BlockerType } from './types';
import { ageInDays } from './card-age';

/**
 * A BLOCKER FIELD THAT SAYS "NO" IS AN EMPTY BLOCKER FIELD.
 *
 * The field asks "what is holding this up, if anything", and the honest answer
 * to "if anything" is very often the word *no*. Every assistant writing a
 * tracker file reaches for it, and so does anybody typing into the box on the
 * card. Read literally, that word IS a blocker, so the card is flagged as
 * waiting on something called "No".
 *
 * WHAT IT COST, on this board, on 27 August 2026: 78 of 300 cards were flagged
 * as blocked and 3 actually were. Blocked work is hidden from "what should I do
 * next", so a quarter of the board had quietly disappeared from the one screen
 * somebody looks at to decide what to do — and every card said, in writing,
 * that it was waiting on nothing.
 *
 * WHY THE ANSWER IS HERE AND NOT A RULE ABOUT WRITING FILES. A rule that people
 * have to remember is a rule that fails, and this one had already failed for a
 * week across several sessions. The word is understood instead.
 *
 * ONLY THE WHOLE FIELD, never a prefix. "None — the designs were agreed on 22
 * August" is somebody making an argument, and throwing away their sentence
 * because it opens with "none" would be a worse fault than the one being fixed.
 */
const MEANS_NOTHING = /^(no|none|n\/a|na|nil|nope|nothing|not blocked|false|-|–|—)[.!]?$/i;

export function saysNothing(text: string | undefined | null): boolean {
  const t = (text ?? '').trim();
  return t === '' || MEANS_NOTHING.test(t);
}

/**
 * EVERY blocker on a card, as a list — the source of truth for the blocker
 * model (#456), with a derive fallback for cards written before it.
 *
 * When `blockerRefs` is set it IS the list (filtered to entries that say
 * something or name something). When it is absent — every card from before
 * migration 0157, and any the new UI has not rewritten — a one-entry list is
 * derived from the scalar `blocked` / `blockerType` / `blockerTarget`, so
 * nothing reads differently the day this ships. The derive mirrors how the modal
 * writes the scalar: an `item` blocker's target is a card ref, a `person`
 * blocker's is a person, and every other kind carries its target inside the
 * words (no structured ref).
 */
export function blockersOf(
  issue: Pick<Issue, 'blocked' | 'blockerType' | 'blockerTarget' | 'blockerRefs'>
): BlockerEntry[] {
  const list = issue.blockerRefs;
  if (Array.isArray(list)) {
    return list.filter((e) => !!e && (!saysNothing(e.text) || (e.refs?.length ?? 0) > 0));
  }
  const text = blockerOf(issue);
  if (!text) return [];
  const target = issue.blockerTarget?.trim();
  const type = (issue.blockerType || 'other') as BlockerType;
  // A target that is a whitespace-free token carrying a digit (#116, FDD-123, B4)
  // is a CARD reference — matched against the board exactly as the single blocker
  // always was, so a finished target still clears the block regardless of the
  // recorded kind. A `person` blocker's target is the person; any other
  // free-text target (a decision, an outside party) rides inside `text`.
  const looksLikeCard = !!target && !/\s/.test(target) && /\d/.test(target);
  const refs: BlockerRef[] = looksLikeCard
    ? [{ kind: 'card', ref: target! }]
    : target && type === 'person'
      ? [{ kind: 'person', ref: target, label: target }]
      : [];
  return [{ type, text, refs }];
}

/**
 * SPLITTING A BLOCKER LIST INTO THE TWO SECTIONS THAT SHOW IT.
 *
 * Card-to-card dependencies live in "Depends on", everything else — a person, a
 * decision, a vendor, a free-text reason — in "What's blocking this". But one
 * stored entry can legacy-mix a card ref with a person ref: the single-kind
 * entry the UI now writes is newer than the data. So each section takes a
 * PROJECTION of every entry down to its own kind of ref — a mixed entry shows
 * its card above and its person below, nothing hides, and the next save writes
 * it back as the two single-kind entries the model now creates. Both are pure
 * and total, so an untouched mixed entry is only ever split once a person edits
 * one of the two sections (the modal keeps the raw list until then).
 *
 * The reason text rides with the NON-CARD side: a card link carries its meaning
 * in its link type, not in prose (the dependency editor never renders `text`),
 * so a legacy card entry that also held a reason keeps that reason where it is
 * read — as a text blocker — rather than dropping it.
 */
export function cardDepsOf(entries: BlockerEntry[]): BlockerEntry[] {
  return entries
    .map((e) => ({ ...e, text: '', refs: e.refs.filter((r) => r.kind === 'card') }))
    .filter((e) => e.refs.length > 0);
}
export function otherBlockersOf(entries: BlockerEntry[]): BlockerEntry[] {
  return entries
    .map((e) => ({ ...e, refs: e.refs.filter((r) => r.kind !== 'card') }))
    .filter((e) => e.refs.length > 0 || (e.text ?? '').trim() !== '');
}

/**
 * Does ONE entry still block? An entry that names cards is cleared only when
 * EVERY named card is done or won't-do — finish one of two and the wait on the
 * other remains. An entry that names a person, or names nothing checkable (a
 * decision, a test, an outside party), cannot be auto-resolved and stays
 * blocking until a person clears it — the rule the single blocker always had.
 */
function entryStillBlocks(e: BlockerEntry, all: Issue[]): boolean {
  const cardRefs = e.refs.filter((r) => r.kind === 'card');
  if (cardRefs.length === 0) return true;
  return cardRefs.some((r) => {
    const card = all.find((i) => i.ref === r.ref);
    if (!card) return true; // named but not found — leave it blocked, never guess it away
    return card.status !== 'done' && card.status !== 'wontdo';
  });
}

/**
 * IS THIS REALLY STILL BLOCKED?
 *
 * A blocker is something somebody typed, and it does not update itself. When the
 * thing being waited on is a named card and that card is finished, the wait is
 * over — so the board must stop calling blocked a card that nothing is blocking,
 * or real, startable work quietly disappears from "what should I do next". A
 * card is still blocked while ANY of its blocker entries is still blocking; an
 * entry naming only finished cards has cleared. For a card written before the
 * list existed this reads its one derived entry, so the behaviour is unchanged.
 */
export function stillBlocked(issue: Issue, all: Issue[]): boolean {
  // Deliberately INDEPENDENT of the card's own status: this answers "is its
  // blocker unresolved", which is what the Blocked VIEW reads — and that view
  // keeps a card that was blocked and then finished ("still worth seeing", a
  // decision the views test enshrines). The separate rule that a FINISHED card
  // shows no "Blocked" FLAG is a card-surface concern (blockedBadge, the modal
  // banner, the metrics count), applied there, not folded in here.
  return entriesStillBlock(blockersOf(issue), all);
}

/**
 * Do any of these blocker ENTRIES still block, given the board?
 *
 * The shared predicate behind `stillBlocked` and the edit-time "Blocked" flag in
 * BlockerSection, lifted out so the authoring surface stops reading a resolved
 * blocker as a live one while the read surfaces already don't (L165) — the same
 * presence-vs-resolution split as L157/L160, one rule for both. An empty list
 * blocks nothing; an entry that names only finished cards has cleared; an entry
 * that names a person or nothing checkable stays blocking until a person clears it.
 */
export function entriesStillBlock(entries: BlockerEntry[], all: Issue[]): boolean {
  return entries.length > 0 && entries.some((e) => entryStillBlocks(e, all));
}

/** The issues that are genuinely waiting on something. */
export const reallyBlocked = (issues: Issue[]): Issue[] =>
  issues.filter((i) => stillBlocked(i, issues));

/**
 * Clearing a DECISION blocker — the field patch that drops every decision-kind
 * entry and re-mirrors the legacy scalar fields from the first remaining entry.
 * The same write IssueModal's clearDecisionBlocker makes, lifted out so the
 * board's "do this next" clear strips the STRUCTURED entry too (not just the old
 * scalars, which left a stale blocker_refs decision behind), and so the two paths
 * cannot drift. An empty remainder clears all four fields.
 */
export function patchWithoutDecision(entries: BlockerEntry[]): {
  blockerRefs: BlockerEntry[];
  blocked: string;
  blockerType: BlockerType | '';
  blockerTarget: string;
} {
  const remaining = entries.filter((e) => e.type !== 'decision');
  const p = remaining[0];
  const pCard = p?.refs.find((r) => r.kind === 'card');
  const pPerson = p?.refs.find((r) => r.kind === 'person');
  return {
    blockerRefs: remaining,
    blocked: p
      ? p.text.trim() ||
        (pCard
          ? `${pCard.ref}${pCard.label ? ` ${pCard.label}` : ''}`
          : pPerson
            ? `Waiting on ${pPerson.label || pPerson.ref}`
            : '')
      : '',
    blockerType: (p ? p.type || 'other' : '') as BlockerType | '',
    blockerTarget: pCard?.ref || pPerson?.label || pPerson?.ref || '',
  };
}

/**
 * THE BLOCKER TO SHOW, or nothing.
 *
 * Every screen that draws a blocker reads this rather than the field, because
 * the field is what somebody typed and this is what it means. Reading the field
 * directly is how a card kept a red "Waiting on something else" lozenge whose
 * stated reason was the word "No" — the counts had been taught the rule and the
 * card had not, which is the worst possible split: the number says one thing
 * and the thing the number is about says the other.
 */
export function blockerOf(issue: { blocked?: string | null }): string {
  return saysNothing(issue.blocked) ? '' : String(issue.blocked).trim();
}

/**
 * THE ONE-LINE BLOCKED BADGE FOR THE CARD FACE.
 *
 * A blocked card used to state its block up to three ways — the flag, the flag's
 * summary sentence, and a four-line reason paragraph — so blocked cards towered
 * over the rest and the column stopped scanning as a list. This is the terse
 * form the card-display review asked for: the word, HOW LONG (from `blockedAt`,
 * migration 0098), and the card it waits on when that is named — with the full
 * typed reason kept for the hover title and the open card, so nothing is lost.
 *
 * `text` is built to FIT ON ONE LINE: only a card reference or a genuinely short
 * target rides onto the face, because the whole point is that the badge never
 * has to be clipped. Returns null when the card is not really blocked — the word
 * "no" is not a blocker (`blockerOf`).
 */
/**
 * WHEN A BLOCK IS OLD ENOUGH TO GO RED — the app's own two-week line.
 *
 * The portfolio's health and the per-person grade both age a blocker on the
 * same scale: over a week is amber, over two weeks is red (`portfolio.ts`,
 * `accountability.ts`). The card face escalates on that RED line. Below it the
 * flag stays the ordinary orange — which already reads as "attention" and
 * already carries the day count, so a faint amber step would say nothing an
 * orange lozenge does not already say. At a fortnight the flag turns red, so a
 * long-stuck block cannot hide behind the everyday flag and the card agrees
 * with the rollups at the point that matters. It cannot be back-filled, which
 * is why the timestamp (`blocked_at`, migration 0099) is captured the moment a
 * card is blocked and only read here.
 */
export const BLOCK_STUCK_DAYS = 14;

export function blockedBadge(
  issue: Issue,
  now: number,
  all?: Issue[]
): { text: string; full: string; stuck: boolean } | null {
  // A FINISHED card never wears a "Blocked" flag on its face — it is done, there
  // is nothing left waiting (the card half of L157). The Blocked VIEW still lists
  // such a card for review, but the face does not flag it.
  if (issue.status === 'done' || issue.status === 'wontdo') return null;
  // When the board's cards are passed, a blocker whose named card is finished is
  // no longer a block — the same rule Next up reads (stillBlocked) — so the card
  // face never says "Blocked" for a card nothing is blocking. Without `all` — the
  // badge's own formatting unit tests — it reads the field exactly as before.
  if (all && !stillBlocked(issue, all)) return null;
  const full = blockerOf(issue);
  if (!full) return null;
  const days = ageInDays(issue.blockedAt, now);
  const target = issue.blockerTarget?.trim();
  const tag = target && (/#\d/.test(target) || target.length <= 14) ? target : '';
  const text = 'Blocked' + (days !== null ? ` ${days}d` : '') + (tag ? ` · ${tag}` : '');
  return { text, full, stuck: days !== null && days > BLOCK_STUCK_DAYS };
}

/**
 * WHAT THE BLOCKED FIELD OFFERS AS YOU TYPE.
 *
 * A card can be blocked by another card, or by a person on the board — so the
 * field is a typeahead. Given what has been typed, this returns the open cards
 * and the board-people that match, cards first; the card modal renders them and,
 * when one is picked, records it (blockerType + blockerTarget). It is pure and
 * lives here, beside the rest of the blocker rules, so it can be tested without
 * the modal.
 *
 * The rules that matter:
 *  · Nothing under two characters, so a half-typed *reason* does not flash a menu.
 *  · Never the card itself, and never a card that is done or won't-do — linking to
 *    a finished card would create the exact stale blocker `stillBlocked` exists to
 *    catch, at the moment it is made.
 *  · Cards match on ref OR title; people match on name.
 *  · `peopleNames` is passed in already reduced to those who can open the board —
 *    the same list the Assignee field uses — never the whole workspace.
 */
export type BlockSuggestion =
  | { key: string; kind: 'card'; card: Issue }
  | { key: string; kind: 'person'; name: string };

export function blockedSuggestions(
  query: string,
  issues: Issue[],
  peopleNames: string[],
  selfId?: string,
  caps: { cards?: number; people?: number } = {}
): BlockSuggestion[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2) return [];
  const cards: BlockSuggestion[] = issues
    .filter((i) => i.id !== selfId && i.status !== 'done' && i.status !== 'wontdo')
    .filter((i) => i.ref.toLowerCase().includes(q) || i.title.toLowerCase().includes(q))
    .slice(0, caps.cards ?? 6)
    .map((i) => ({ key: `c:${i.id}`, kind: 'card', card: i }));
  const people: BlockSuggestion[] = peopleNames
    .filter((n) => n.toLowerCase().includes(q))
    .slice(0, caps.people ?? 4)
    .map((n) => ({ key: `p:${n}`, kind: 'person', name: n }));
  return [...cards, ...people];
}
